<template>
  <a-modal
    title="报告详情"
    :visible="visible"
    :width="1120"
    :footer="null"
    :body-style="{ padding: 0 }"
    wrap-class-name="abas-report-detail-modal"
    centered
    @cancel="cancel"
  >
    <a-spin :spinning="loading">
      <div v-if="adaptiveAssessment" class="detail-shell">
        <div class="report-overview">
          <div class="overview-identity">
            <div class="name-avatar">{{ nameInitial }}</div>
            <div class="overview-copy">
              <div class="overview-title-row">
                <h2>{{ adaptiveAssessment.name || '未命名被评人' }}</h2>
                <span class="edition-tag">{{ assessmentTitle(adaptiveAssessment) }}</span>
                <span class="input-mode-tag" :class="{ detailed: hasDetailScores(adaptiveAssessment) }">
                  {{ hasDetailScores(adaptiveAssessment) ? '逐题录入' : '手工录入' }}
                </span>
              </div>
              <div class="overview-meta">
                <span><a-icon type="file-text" />记分编号 {{ formatValue(adaptiveAssessment.scoring_num) }}</span>
                <span><a-icon type="calendar" />测试日期 {{ formatValue(adaptiveAssessment.test_date) }}</span>
                <span><a-icon type="user" />主试 {{ formatValue(adaptiveAssessment.examiner_name) }}</span>
              </div>
            </div>
          </div>
          <div class="overview-age">
            <span>实际年龄</span>
            <strong>{{ formatValue(adaptiveAssessment.real_age) }}</strong>
          </div>
        </div>

        <div class="detail-content">
          <section class="detail-section">
            <div class="section-heading">
              <span class="section-icon"><a-icon type="profile" /></span>
              <div><h3>基本信息</h3><p>被评人身份与本次测评资料</p></div>
            </div>
            <div class="info-grid">
              <div v-for="item in baseItems" :key="item.label" class="info-item">
                <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
              </div>
            </div>
          </section>

          <assessment-section :assessment="adaptiveAssessment" :title="assessmentTitle(adaptiveAssessment)" />

          <section v-if="hasDetailScores(adaptiveAssessment)" class="detail-section">
            <div class="section-heading">
              <span class="section-icon detail"><a-icon type="appstore" /></span>
              <div><h3>详细分数</h3><p>按领域查看各题得分</p></div>
            </div>
            <abas-score-detail
              :value="detailScores(adaptiveAssessment)"
              :edition="String(adaptiveAssessment.edition)"
              :scoring-type="String(adaptiveAssessment.scoring_type)"
              readonly
            />
          </section>

          <template v-if="otherAdaptiveAssessment">
            <div class="related-divider">
              <span>关联报告</span>
              <strong>{{ assessmentTitle(otherAdaptiveAssessment) }}</strong>
            </div>
            <assessment-section :assessment="otherAdaptiveAssessment" :title="assessmentTitle(otherAdaptiveAssessment)" />
            <section v-if="hasDetailScores(otherAdaptiveAssessment)" class="detail-section">
              <div class="section-heading">
                <span class="section-icon detail"><a-icon type="appstore" /></span>
                <div><h3>关联报告详细分数</h3><p>按领域查看各题得分</p></div>
              </div>
              <abas-score-detail
                :value="detailScores(otherAdaptiveAssessment)"
                :edition="String(otherAdaptiveAssessment.edition)"
                :scoring-type="String(otherAdaptiveAssessment.scoring_type)"
                readonly
              />
            </section>
          </template>
        </div>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import {Detail} from "../../commonApi";
import AbasScoreDetail from "../abas-score-detail";

const SCORE_FIELDS = [
  {key: 'score_communicate', label: '沟通'},
  {key: 'score_community', label: '社区应用'},
  {key: 'score_study', label: '学习功能'},
  {key: 'score_family', label: '居家 / 学校生活'},
  {key: 'score_healthy', label: '健康与安全'},
  {key: 'score_leisure', label: '休闲'},
  {key: 'score_self_care', label: '自我照顾'},
  {key: 'score_self_guidance', label: '自我管理'},
  {key: 'score_social', label: '社交'},
  {key: 'score_skills', label: '动作技巧'},
  {key: 'score_work', label: '工作'},
];

const AssessmentSection = {
  props: {assessment: Object, title: String},
  computed: {
    settingItems() {
      const data = this.assessment || {};
      return [
        {label: '评定类型', value: this.title},
        {label: '所在年级', value: data.grade},
        {label: '学校 / 单位', value: data.school_name},
        {label: '家长文化', value: data.parent_culture},
        {label: '置信区间', value: data.confidence_probability ? `${String(data.confidence_probability).replace(/%$/, '')}%` : ''},
        {label: '显著性水平', value: data.significance_level},
      ];
    },
    scoreItems() {
      return SCORE_FIELDS.filter((item) => {
        const value = this.assessment && this.assessment[item.key];
        return value !== null && value !== undefined && value !== '';
      }).map((item) => ({
        ...item,
        label: item.key === 'score_family'
          ? (String(this.assessment.edition) === '3' || String(this.assessment.scoring_type) === '1'
            ? '居家生活' : '学校生活')
          : item.label,
        value: this.assessment[item.key],
      }));
    },
  },
  methods: {
    formatValue(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },
  },
  template: `
    <section class="detail-section assessment-section">
      <div class="section-heading">
        <span class="section-icon score"><a-icon type="bar-chart" /></span>
        <div><h3>{{ title }}</h3><p>评估设置与各领域原始分</p></div>
      </div>
      <div class="parameter-grid">
        <div v-for="item in settingItems" :key="item.label" class="parameter-item">
          <span>{{ item.label }}</span><strong>{{ formatValue(item.value) }}</strong>
        </div>
      </div>
      <div class="score-heading"><span>领域原始分</span><small>{{ scoreItems.length }} 项</small></div>
      <div class="score-grid">
        <div v-for="item in scoreItems" :key="item.key" class="score-item">
          <span>{{ item.label }}</span><strong>{{ item.value }}</strong>
        </div>
      </div>
    </section>
  `,
};

export default {
  components: {AbasScoreDetail, AssessmentSection},
  props: {
    item: Object,
    detailVisible: Boolean,
  },
  data() {
    return {
      visible: false,
      loading: false,
      id: '',
      adaptiveAssessment: null,
      otherAdaptiveAssessment: null,
    };
  },
  computed: {
    nameInitial() {
      return this.adaptiveAssessment && this.adaptiveAssessment.name
        ? this.adaptiveAssessment.name.slice(0, 1) : '评';
    },
    baseItems() {
      const data = this.adaptiveAssessment || {};
      return [
        {label: '个案编号', value: this.formatValue(data.archives_num)},
        {label: '记分编号', value: this.formatValue(data.scoring_num)},
        {label: '被评人姓名', value: this.formatValue(data.name)},
        {label: '主试姓名', value: this.formatValue(data.examiner_name)},
        {label: '性别', value: this.genderLabel(data.gender)},
        {label: '出生日期', value: this.formatValue(data.born_time)},
        {label: '实际年龄', value: this.formatValue(data.real_age)},
        {label: '少数民族', value: this.yesNoLabel(data.is_minorities)},
        {label: '用手习惯', value: this.handHabitLabel(data.hand_habit)},
        {label: '测试地点', value: this.formatValue(data.exam_address)},
        {label: '备注', value: this.formatValue(data.remark)},
      ];
    },
  },
  watch: {
    item: {
      handler(value) {
        if (!value) return;
        this.id = value.id;
        this.adaptiveAssessment = value;
      },
      immediate: true,
      deep: true,
    },
    detailVisible: {
      handler(value) {
        this.visible = value;
        if (value && this.id) this.getDetail();
      },
      immediate: true,
    },
  },
  methods: {
    formatValue(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },
    genderLabel(value) {
      return String(value) === '1' ? '男' : String(value) === '2' ? '女' : '-';
    },
    yesNoLabel(value) {
      return String(value) === '1' ? '否' : String(value) === '2' ? '是' : '-';
    },
    handHabitLabel(value) {
      return String(value) === '1' ? '左手' : String(value) === '2' ? '右手' : '-';
    },
    assessmentTitle(data) {
      if (!data) return '-';
      const edition = {1: '幼儿版', 2: '儿童版', 3: '成人版'}[String(data.edition)] || '未知版本';
      const scoring = String(data.edition) === '3'
        ? (String(data.scoring_type) === '1' ? '自评' : '他评')
        : (String(data.scoring_type) === '1' ? '家长评' : '教师评');
      return `${scoring}-${edition}`;
    },
    detailScores(data) {
      if (!data) return {};
      if (data.detail_scores && typeof data.detail_scores === 'object') return data.detail_scores;
      if (typeof data.detail_score_json === 'string' && data.detail_score_json) {
        try {
          return JSON.parse(data.detail_score_json);
        } catch (e) {
          return {};
        }
      }
      return {};
    },
    hasDetailScores(data) {
      return data && String(data.detail_score_enabled) === '1'
        && Object.keys(this.detailScores(data)).length > 0;
    },
    async getDetail() {
      this.loading = true;
      try {
        const res = await Detail({id: this.id});
        if (res && res.code == 0) {
          this.adaptiveAssessment = res.adaptiveAssessment;
          this.otherAdaptiveAssessment = res.otherAdaptiveAssessment || null;
        }
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.visible = false;
      this.$emit('detailCancel');
    },
  },
};
</script>

<style scoped>
.detail-shell { color: #27313a; background: #f5f7f8; }
.report-overview {
  display: flex; min-height: 112px; align-items: center; justify-content: space-between;
  padding: 22px 30px; color: #fff; background: #3c263a;
}
.overview-identity, .overview-title-row, .overview-meta { display: flex; align-items: center; }
.overview-identity { min-width: 0; gap: 16px; }
.name-avatar {
  width: 54px; height: 54px; flex: 0 0 54px; border: 1px solid rgba(255,255,255,.28);
  border-radius: 50%; color: #832c75; background: #fff; font-size: 22px; font-weight: 700;
  line-height: 52px; text-align: center;
}
.overview-copy { min-width: 0; }
.overview-title-row { flex-wrap: wrap; gap: 8px; }
.overview-title-row h2 { margin: 0 4px 0 0; color: #fff; font-size: 22px; line-height: 30px; }
.edition-tag, .input-mode-tag { padding: 3px 9px; border-radius: 4px; font-size: 12px; line-height: 20px; }
.edition-tag { background: rgba(255,255,255,.16); }
.input-mode-tag { color: #d7e8e5; background: rgba(67,137,127,.3); }
.input-mode-tag.detailed { color: #fff1d7; background: rgba(185,128,37,.34); }
.overview-meta { flex-wrap: wrap; gap: 8px 20px; margin-top: 10px; color: rgba(255,255,255,.72); font-size: 12px; }
.overview-meta .anticon { margin-right: 6px; }
.overview-age { display: flex; min-width: 150px; align-items: flex-end; margin-left: 20px; flex-direction: column; }
.overview-age span { color: rgba(255,255,255,.65); font-size: 12px; }
.overview-age strong { margin-top: 6px; color: #fff; font-size: 17px; }
.detail-content { max-height: calc(100vh - 210px); overflow-y: auto; padding: 0 30px 30px; }
.detail-section { padding: 28px 0; border-bottom: 1px solid #dde2e5; }
.section-heading { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.section-icon { display: grid; width: 38px; height: 38px; flex: 0 0 38px; place-items: center; border-radius: 6px; color: #832c75; background: #eee5ed; font-size: 17px; }
.section-icon.score { color: #28685f; background: #e1eeeb; }
.section-icon.detail { color: #3c5f83; background: #e3ebf3; }
.section-heading h3 { margin: 0; color: #27313a; font-size: 17px; line-height: 24px; }
.section-heading p { margin: 2px 0 0; color: #8a939a; font-size: 12px; }
.info-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); border: 1px solid #e1e5e7; border-radius: 6px; background: #fff; }
.info-item { display: flex; min-height: 72px; justify-content: center; padding: 14px 16px; border-right: 1px solid #e8ebed; border-bottom: 1px solid #e8ebed; flex-direction: column; }
.info-item:nth-child(4n) { border-right: 0; }
.info-item:nth-last-child(-n+3) { border-bottom: 0; }
.info-item span, .parameter-item span { color: #8a939a; font-size: 12px; }
.info-item strong, .parameter-item strong { margin-top: 6px; color: #27313a; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.related-divider { display: flex; align-items: center; gap: 12px; padding: 24px 0 0; color: #8a939a; }
.related-divider::before, .related-divider::after { height: 1px; flex: 1; background: #d6dde0; content: ''; }
.related-divider strong { color: #832c75; }
.detail-section ::v-deep .detail-score-panel { margin: 0; }
.assessment-section ::v-deep .parameter-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); border: 1px solid #e1e5e7; border-radius: 6px; background: #fff; }
.assessment-section ::v-deep .parameter-item { display: flex; min-height: 72px; justify-content: center; padding: 14px 16px; border-right: 1px solid #e8ebed; border-bottom: 1px solid #e8ebed; flex-direction: column; }
.assessment-section ::v-deep .parameter-item span { color: #8a939a; font-size: 12px; }
.assessment-section ::v-deep .parameter-item strong { margin-top: 6px; color: #27313a; font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
.assessment-section ::v-deep .parameter-item:nth-child(3n) { border-right: 0; }
.assessment-section ::v-deep .parameter-item:nth-last-child(-n+3) { border-bottom: 0; }
.assessment-section ::v-deep .score-heading { display: flex; align-items: center; justify-content: space-between; margin: 22px 0 9px; color: #5d666d; font-size: 13px; font-weight: 600; }
.assessment-section ::v-deep .score-heading small { color: #a1a8ad; font-weight: 400; }
.assessment-section ::v-deep .score-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 10px; }
.assessment-section ::v-deep .score-item { display: flex; min-height: 66px; align-items: center; justify-content: space-between; padding: 0 15px; border: 1px solid #e1e5e7; border-radius: 6px; background: #fff; }
.assessment-section ::v-deep .score-item span { color: #6f777d; font-size: 13px; }
.assessment-section ::v-deep .score-item strong { margin-left: 12px; color: #27313a; font-size: 21px; }
@media (max-width: 900px) {
  .info-grid, .assessment-section ::v-deep .score-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .info-item:nth-child(4n), .assessment-section ::v-deep .parameter-item:nth-child(3n) { border-right: 1px solid #e8ebed; }
  .info-item:nth-child(2n), .assessment-section ::v-deep .parameter-item:nth-child(2n) { border-right: 0; }
  .assessment-section ::v-deep .parameter-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
}
@media (max-width: 600px) {
  .report-overview { align-items: flex-start; padding: 18px; flex-direction: column; }
  .name-avatar { width: 44px; height: 44px; flex-basis: 44px; font-size: 18px; line-height: 42px; }
  .overview-title-row h2 { font-size: 19px; }
  .overview-meta { align-items: flex-start; flex-direction: column; gap: 5px; }
  .overview-age { align-items: flex-start; margin: 15px 0 0 60px; }
  .detail-content { max-height: calc(100vh - 190px); padding: 0 18px 20px; }
  .info-grid, .assessment-section ::v-deep .parameter-grid, .assessment-section ::v-deep .score-grid { grid-template-columns: 1fr; }
  .info-item, .assessment-section ::v-deep .parameter-item { border-right: 0 !important; border-bottom: 1px solid #e8ebed !important; }
  .info-item:last-child, .assessment-section ::v-deep .parameter-item:last-child { border-bottom: 0 !important; }
}
</style>

<style>
.abas-report-detail-modal .ant-modal { max-width: calc(100vw - 24px); }
.abas-report-detail-modal .ant-modal-header { padding: 16px 22px; border-bottom-color: #e8ebed; }
.abas-report-detail-modal .ant-modal-title { color: #27313a; font-weight: 600; }
.abas-report-detail-modal .ant-modal-close-x { width: 54px; height: 54px; line-height: 54px; }
</style>
